import { useQuery } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Col, Empty, List, Row, Space, Spin, Tabs, Tag, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { apiFetch } from "../api/client";
import { formatDate, formatDateTime } from "../api/format";
import type {
  AssignationRowBase,
  ListResponse,
  MainIndexState,
  MessageRow,
  NewsOverviewRow,
} from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { ArchiveTable } from "./ArchivePage";

/**
 * The landing screen — the old application's `main/_home` partial.
 *
 * It shows four things, all of which are read from endpoints that already
 * existed for other screens:
 *
 * - the news of the user's own circumscriptions (news_overview_grid_xml),
 * - the messages that arrived since the previous login — the same set the
 *   sidebar badge counts,
 * - for a speaker: the next weeks of their own calendar (speaker_preview_grid_xml),
 * - for a local coordinator: the next weeks of the congregation's calendar
 *   (congregation_preview_grid_xml).
 *
 * Which of the two previews appear is decided by /main/index: it names the
 * congregation behind each role and answers null for a role the user does not
 * hold. The client must not re-derive that from anything else.
 */
export function HomePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const landing = useQuery({
    queryKey: ["main", "index"],
    queryFn: () => apiFetch<MainIndexState>("/main/index"),
  });

  if (landing.isPending) return <Spin />;
  if (landing.error) {
    return (
      <Alert
        type="error"
        showIcon
        title={landing.error instanceof Error ? landing.error.message : String(landing.error)}
      />
    );
  }

  const speakerCongregation = landing.data?.speaker_congregation ?? null;
  const coordinatorCongregation = landing.data?.local_coordinator_congregation ?? null;

  return (
    <Space orientation="vertical" size="middle" style={{ display: "flex" }}>
      <Card>
        <Space orientation="vertical" size="small">
          {user && (
            <Typography.Title level={4} style={{ margin: 0 }}>
              {user.first_name} {user.last_name}
            </Typography.Title>
          )}

          {landing.data?.pending_invitations ? (
            <Space orientation="vertical">
              <Typography.Text>{t("invitation.pending_invitations_info")}</Typography.Text>
              <Button type="primary" onClick={() => navigate("/confirm-changes")}>
                {t("invitation.pending_invitations")}
              </Button>
            </Space>
          ) : (
            <Typography.Text type="secondary">{t("app.no_pending_invitations")}</Typography.Text>
          )}
        </Space>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={14}>
          <NewsPanel />
        </Col>
        <Col xs={24} xl={10}>
          <MessagesPanel />
        </Col>
      </Row>

      {(speakerCongregation || coordinatorCongregation) && (
        <EventPreviews
          speakerCongregation={speakerCongregation}
          coordinatorCongregation={coordinatorCongregation}
        />
      )}
    </Space>
  );
}

/** The news of the circumscriptions the user belongs to, newest window first. */
function NewsPanel() {
  const { t } = useTranslation();

  const news = useQuery({
    queryKey: ["news", "overview"],
    queryFn: () => apiFetch<ListResponse<NewsOverviewRow>>("/news/overview"),
  });

  return (
    <Card title={t("news.news_header")} styles={{ body: { maxHeight: 340, overflowY: "auto" } }}>
      {news.isPending ? (
        <Spin />
      ) : news.error ? (
        <Alert
          type="error"
          showIcon
          title={news.error instanceof Error ? news.error.message : String(news.error)}
        />
      ) : (news.data?.data.length ?? 0) === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          size="small"
          dataSource={news.data?.data ?? []}
          renderItem={(item) => (
            <List.Item>
              <Space orientation="vertical" size={2} style={{ width: "100%" }}>
                <Space size={8}>
                  <Typography.Text strong={item.modified}>
                    {item.circumscription_code}
                  </Typography.Text>
                  {item.modified && <Tag color="green">{t("icon.modified")}</Tag>}
                  <Typography.Text type="secondary">
                    {formatDate(item.date_from)} – {formatDate(item.date_to)}
                  </Typography.Text>
                </Space>
                {/* The content is free text a coordinator typed; it is printed
                    as text, never as markup. */}
                <Typography.Paragraph style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {item.content}
                </Typography.Paragraph>
              </Space>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

/**
 * The messages that arrived since the previous login. `new` is the server's
 * answer to that question (MessageController#manage_message_grid) and is what
 * the sidebar badge counts — deliberately not `viewed_at`, which is the
 * separate read/unread state.
 */
function MessagesPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const messages = useQuery({
    queryKey: ["messages"],
    queryFn: () => apiFetch<ListResponse<MessageRow>>("/message/manage_message_grid"),
  });

  const fresh = (messages.data?.data ?? []).filter((row) => row.new);

  return (
    <Card
      title={
        <Space>
          {t("home.received_notifications")}
          {fresh.length > 0 && <Badge count={fresh.length} />}
        </Space>
      }
      extra={
        <Button type="link" onClick={() => navigate("/message/index")}>
          {t("navigation.messages")}
        </Button>
      }
      styles={{ body: { maxHeight: 340, overflowY: "auto" } }}
    >
      {messages.isPending ? (
        <Spin />
      ) : messages.error ? (
        <Alert
          type="error"
          showIcon
          title={messages.error instanceof Error ? messages.error.message : String(messages.error)}
        />
      ) : fresh.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          size="small"
          dataSource={fresh}
          renderItem={(item) => (
            <List.Item
              style={{ cursor: "pointer" }}
              onClick={() => navigate("/message/index")}
            >
              <Space orientation="vertical" size={0}>
                <Typography.Text strong={!item.viewed_at}>{item.subject}</Typography.Text>
                <Typography.Text type="secondary">
                  {formatDateTime(item.created_at)}
                </Typography.Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

/**
 * The two calendar previews. Both endpoints stop after the role's
 * `preview_forward_weeks`, which is what makes them previews rather than the
 * calendar screens the sidebar leads to.
 */
function EventPreviews({
  speakerCongregation,
  coordinatorCongregation,
}: {
  speakerCongregation: string | null;
  coordinatorCongregation: string | null;
}) {
  const { t } = useTranslation();

  const speaker = useQuery({
    queryKey: ["speaker_assignation", "preview"],
    queryFn: () =>
      apiFetch<ListResponse<AssignationRowBase>>("/speaker_assignation/preview"),
    enabled: speakerCongregation !== null,
  });

  const congregation = useQuery({
    queryKey: ["local_assignation", "preview"],
    queryFn: () => apiFetch<ListResponse<AssignationRowBase>>("/local_assignation/preview"),
    enabled: coordinatorCongregation !== null,
  });

  const items = [];

  if (speakerCongregation !== null) {
    items.push({
      key: "speaker",
      label: `${t("home.speaker_events")} – ${speakerCongregation}`,
      children: (
        <ArchiveTable
          scope="speaker"
          hint={false}
          rows={speaker.data?.data ?? []}
          loading={speaker.isPending}
          error={speaker.error}
        />
      ),
    });
  }

  if (coordinatorCongregation !== null) {
    items.push({
      key: "congregation",
      label: `${t("home.congregation_events")} – ${coordinatorCongregation}`,
      children: (
        <ArchiveTable
          scope="congregation"
          hint={false}
          rows={congregation.data?.data ?? []}
          loading={congregation.isPending}
          error={congregation.error}
        />
      ),
    });
  }

  return (
    <Card>
      <Tabs items={items} />
    </Card>
  );
}
